<template>
    <div v-show="!isLoading" class="room-settings">
        <div class="tab-pane active">
            <div v-if="aAreas.length === 0" class="data-empty">
                <h3>{{ $t('roomSettings.noAssignmentAreas') }}</h3>
            </div>
            <div v-else>
                <div class="search-text-wrapper no-print">
                    <h5 v-if="searchKeyword" v-show="!searchKeyword || !nothingFound">
                        <!-- eslint-disable -->
                        {{ $t('roomConfiguration.search.wasFound') }}
                        {{ countedItems[0] }} {{ $t('roomConfiguration.search.corridors') }},
                        {{ countedItems[1] }} {{ $t('roomConfiguration.search.rooms') }},
                        {{ countedItems[2] }} {{ $t('roomConfiguration.search.beds') }},
                        {{ countedItems[3] }} {{ $t('roomConfiguration.search.toilets') }},
                        {{ countedItems[4] }} {{ $t('roomConfiguration.search.modules') }},
                        {{ countedItems[5] }} {{ $t('roomConfiguration.search.others') }}
                        <!-- eslint-enable -->
                    </h5>
                </div>
                <div class="room-settings-wide-screen no-print">
                    <Transition name="slide-fade">
                        <div
                            v-if="!isLoading && isButtonsVisibilityNotToAll"
                            class="window-info-as-message icon-allert"
                        >
                            {{ $t('roomSettings.informationWindow') }}
                        </div>
                    </Transition>
                </div>
                <div class="room-settings-narrow-screen no-print">
                    <div v-if="!isLoading && isButtonsVisibilityNotToAll" class="window-info-as-icon">
                        <span class="room-settings-info no-print">
                            <i class="icon-allert"></i>
                            <div class="tooltiptext">
                                <p>{{ $t('roomSettings.informationWindow') }}</p>
                            </div>
                        </span>
                    </div>
                </div>
                <div class="aarea-selectbox-wrapper no-print">
                    <SelectBox
                        :value="selectedAAreaName"
                        :options="aAreas"
                        :search="false"
                        :placeholder="$t('roomSettings.assignmentArea')"
                        :unsaved-callback="unsavedCallback"
                        @input="selectAArea($event)"
                    />
                </div>
                <div v-if="locations.length > 0" class="table-wrapper no-print">
                    <div v-show="!searchKeyword || !nothingFound" class="table-config">
                        <div class="theader">
                            <div class="location">{{ $t('roomConfiguration.location') }}</div>
                            <div class="actions">
                                {{ $t('roomSettings.visibility') }}
                            </div>
                            <div class="devices">{{ $t('roomConfiguration.devices') }}</div>
                        </div>
                        <div :class="['tbody', { 'hide-tree-lines': !!searchKeyword }]">
                            <RoomSettingsPanel :location="locationsObj" />
                        </div>
                    </div>
                    <h3 v-if="searchKeyword && nothingFound">
                        {{ $t('common.searchNoResult', { val: searchKeyword }) }}
                    </h3>
                </div>
                <div v-else class="data-empty">
                    <h3 v-if="dataLoaded" v-text="$t('roomConfiguration.noRoomConfiguration')"></h3>
                </div>
                <div class="table-wrapper print-only">
                    <h4 v-if="searchKeyword && !nothingFound">
                        {{ $t('print.search', { val: searchKeyword }) }}
                    </h4>
                    <div v-if="locations.length > 0" class="table-config">
                        <h2>{{ $t('print.assignmentArea') + ' ' + selectedAAreaName }}</h2>
                        <div class="theader">
                            <div class="location">{{ $t('roomConfiguration.location') }}</div>
                            <div class="actions">{{ $t('roomSettings.visibility') }}</div>
                            <div class="devices">{{ $t('roomConfiguration.devices') }}</div>
                        </div>
                        <div class="tbody">
                            <RoomSettingsPanel :location="locationsObjCopy" :nothing-found="nothingFound" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapActions, mapMutations, mapState } from 'vuex';
import RoomSettingsPanel from './room-configuration/RoomSettingsPanel.vue';
import SelectBox from './common/SelectBox.vue';
import Location from './room-configuration/Location';
import mappingState from './common/mappingState';
import { ifIsNotCanceled, sortLocations, unsavedHandler } from './common/helpers';
import { resourceAssignmentAreas, resourceModuleButtonsVisibility } from '../api/resources';
import { getCancelToken } from '../api';
import { COMPONENT_NAMES, MAX_MODULES, SUCCESS } from '../constants';

import store from '../store';

export default {
    name: COMPONENT_NAMES.ROOM_SETTINGS,

    components: {
        SelectBox,
        RoomSettingsPanel,
    },

    mixins: [mappingState],

    data() {
        return {
            aAreas: [],
            selectedAAreaName: null,
            locations: [],
            locationsObj: new Location({}),
            locationsCopy: [],
            locationsObjCopy: new Location({}),
            rootLocationId: null,
            loc: [],
            dataLoaded: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['isLoading', 'unsavedChanges']),

        searched() {
            return this.searchKeyword !== ''
                ? this.locations.filter(item => item.name.toLowerCase().includes(this.searchKeyword))
                : null;
        },

        countedItems() {
            let filteredItems = this.searched;
            let aaCounter = 0;
            let toiletCounter = 0;
            let bedCounter = 0;
            let roomCounter = 0;
            let modulesCounter = 0;
            let otherCounter = 0;
            if (filteredItems) {
                filteredItems.forEach(function(item) {
                    switch (item.type) {
                        case 1:
                            aaCounter++;
                            break;
                        case 8:
                            toiletCounter++;
                            break;
                        case 6:
                            roomCounter++;
                            break;
                        case 7:
                            bedCounter++;
                            break;
                        default:
                            otherCounter++;
                            break;
                    }
                });
            }
            if (this.searchKeyword !== '') {
                this.locations.forEach(item => {
                    if (Array.isArray(item.modules)) {
                        item.modules.forEach(module => {
                            if (module.name.toLowerCase().includes(this.searchKeyword)) {
                                modulesCounter++;
                            }
                        });
                    }
                });
            }

            return [aaCounter, roomCounter, bedCounter, toiletCounter, modulesCounter, otherCounter];
        },

        nothingFound() {
            return this.countedItems.reduce((sum, current) => sum + current, 0) === 0;
        },

        selectedAaId() {
            let area = this.aAreas.find(area => area.name === this.selectedAAreaName);
            if (area) {
                return area.id;
            }
            return null;
        },

        isButtonsVisibilityNotToAll() {
            return this.locations.length > 0 && this.locationsObj.isButtonsVisibilityNotToAll();
        },
    },

    watch: {
        locationsObj: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
    },

    created() {
        this.showLoading(true);
        this.aAreas = [];
        this.selectedAAreaName = null;
        resourceAssignmentAreas()
            .then(response => {
                if (Array.isArray(response.data)) {
                    this.aAreas = sortLocations(response.data).map(aarea => ({
                        id: aarea.id,
                        value: aarea.name,
                        name: aarea.name,
                    }));
                    this.selectedAAreaName = this.aAreas[0].name;
                }
            })
            .catch(ifIsNotCanceled())
            .then(() => {
                this.configButton({
                    buttonName: 'saveButton',
                    action: () => this.saveRoomSettings(),
                    disabled: () => !this.unsavedChanges,
                });
            })
            .then(() => {
                this.updateData(() => {
                    return this.loadLocations();
                });
                this.$nextTick(() => {
                    this.loadLocations();
                });
            });
    },

    beforeDestroy() {
        this.progressBarBeforeDestroy();
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),

        ...mapActions({
            progressBarStart: 'progressBar/start',
            progressBarUpdate: 'progressBar/update',
            progressBarHide: 'progressBar/hide',
            progressBarBeforeDestroy: 'progressBar/beforeDestroy',
        }),

        selectAArea(value) {
            this.selectedAAreaName = value;
            this.updateData(() => {
                return this.loadLocations();
            });
            this.$nextTick(() => {
                this.loadLocations();
            });
        },

        loadLocations() {
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
            if (this.aAreas.length === 0) {
                this.showLoading(false);
                return Promise.resolve();
            }
            this.showLoading(true);
            return resourceModuleButtonsVisibility({
                id: this.selectedAaId,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    this.locations = response.data ? sortLocations(response.data) : [];
                    this.locationsObj = this.generateLocationsObj(this.locations);
                    this.locationsCopy = response.data ? response.data : [];
                    this.locationsObjCopy = this.generateLocationsObj(this.locationsCopy);
                    this.rootLocationId = this.locationsObj.location_id;
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                    });
                    this.showLoading(false);
                    this.setSearchEnabled(this.locations.length > 0);
                    this.dataLoaded = true;
                    return response;
                })
                .catch(
                    ifIsNotCanceled({
                        callback: () => {
                            this.locations = [];
                            this.dataLoaded = true;
                            this.setSearchEnabled(this.locations.length > 0);
                        },
                    })
                );
        },

        generateLocationsObj(locations) {
            let locationsObject = new Location(locations.find(item => Number(item.type) === 1));
            locationsObject.children = this.getNestedChildren(locations, locationsObject.location_id);

            return locationsObject;
        },

        getNestedChildren(locations, parent_id) {
            return locations
                .filter(l => l.parent_id === parent_id)
                .map(
                    l =>
                        new Location({
                            ...l,
                            children: this.getNestedChildren(locations, l.location_id),
                        })
                );
        },

        /**
         * Receives single-level list of Locations and splits into array of chunks
         * Chunk consists of locations array
         * Total quantity of modules in all locations inside the chunk is less than const MAX_MODULES
         * One location cannot be splitted if has quantity of modules more than const MAX_MODULES
         * @param list
         * @returns {Array}
         */
        getSplittedLocationsByMaximumModules(list) {
            let chunk = [];
            let countModulesInChunk = 0;
            let splittedSavingList = [];

            // This loop can make step back and re-iterate element
            for (let i = 0; i < list.length; i++) {
                // Current location
                let location = list[i];

                // Checks that the location has less modules than maximum
                if (location.getCountModules() <= MAX_MODULES) {
                    countModulesInChunk += location.getCountModules();

                    // If chunk is not too large, pushes this location in chunk
                    if (countModulesInChunk <= MAX_MODULES) {
                        chunk.push(location);

                        // if it is the last iterate, pushes chunk in common array of chunks.
                        if (i === list.length - 1) {
                            splittedSavingList.push(chunk);
                        }

                        // If chunk is too large, pushes chunk without last location in common array of chunks.
                        // Resets counter and chunk
                        // Makes step back
                    } else {
                        splittedSavingList.push(chunk);
                        countModulesInChunk = 0;
                        chunk = [];

                        // step back
                        i--;
                    }

                    // If this location has more modules than maximum, sets new chunk with only this location
                } else {
                    if (chunk.length > 0) {
                        splittedSavingList.push(chunk);
                        splittedSavingList.push([location]);
                    } else {
                        chunk.push(location);
                        splittedSavingList.push(chunk);
                    }
                    countModulesInChunk = 0;
                    chunk = [];
                }
            }

            return splittedSavingList;
        },

        /**
         * Executes ajax for saving locations chunk by chunk
         * @param chunks
         * @returns {Promise<*>}
         */
        async postChunks(chunks) {
            let processed = 0;
            let total = chunks.reduce((accumulator, chunk) => accumulator + chunk.length, 0);
            this.progressBarStart(this.$t('progressBar.saving'));
            for (let i = 0; i < chunks.length; i++) {
                if (store.state.progressBar.isCanceled) {
                    this.progressBarHide();
                    return Promise.reject('');
                }
                try {
                    let data = JSON.stringify(
                        chunks[i].map(location => location.getButtonsVisibilityChanges())
                    );
                    let response = await resourceModuleButtonsVisibility({
                        method: 'post',
                        id: this.selectedAaId,
                        data,
                        cancelToken: this.cancelToken.token,
                    });
                    processed += response.data.processed;
                } catch (error) {
                    this.progressBarHide();
                    return Promise.reject(error);
                }
                this.progressBarUpdate((processed * 100.0) / total);
            }
            this.progressBarHide();
            // async function returns Promise.resolve(returned value) by default
            return { processed };
        },

        /**
         * Saves changed locations in room configuration
         */
        saveRoomSettings() {
            let savingList = this.locationsObj
                .flattenNestedChildren()
                .filter(location => location.isButtonsVisibilityChanged());
            let chunkList = this.getSplittedLocationsByMaximumModules(savingList);

            this.$nextTick(() => {
                return this.postChunks(chunkList)
                    .then(() => {
                        this.setUnsavedChanges(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                    })
                    .then(() => this.loadLocations())
                    .catch(ifIsNotCanceled());
            });
        },

        unsavedCallback() {
            return new Promise(resolve => {
                let confirmMessage = {
                    confirmBody: this.$t('common.confirmCancelChangesBody'),
                };
                if (this.unsavedChanges) {
                    return unsavedHandler(confirmMessage)
                        .then(() => {
                            resolve();
                        })
                        .catch(() => {});
                }
                resolve();
            });
        },
    },
};
</script>
